import { gsap } from "gsap";

// Variables globales
let currentIndex = 0;
let items = [];
let positionsData = [];
let titleElement;
let isAnimating = false;
let resizeHandler = null;
let navHandlerLeft = null;
let navHandlerRight = null;
let isDestroyed = false; // Flag pour savoir si le module est détruit

let slideHistory = 0; // Sauvegarder la position du carousel entre les pages

// Fonction de nettoyage - appelée avant destruction
export function nonDestroy() {
    // Sauvegarder la position actuelle avant destruction
    slideHistory = currentIndex;

    isDestroyed = true;

    // Arrêter toutes les animations GSAP
    items.forEach(item => {
        if (item.element) {
            gsap.killTweensOf(item.element);
        }
    });

    if (titleElement) {
        gsap.killTweensOf(titleElement);
    }

    // Retirer les event listeners
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
    }

    const btnLeft = document.querySelector('.btn-abcdr-du-non-left');
    const btnRight = document.querySelector('.btn-abcdr-du-non-right');

    if (btnLeft && navHandlerLeft) {
        btnLeft.removeEventListener('click', navHandlerLeft);
    }
    if (btnRight && navHandlerRight) {
        btnRight.removeEventListener('click', navHandlerRight);
    }

    // Nettoyer les variables
    items = [];
    titleElement = null;
    resizeHandler = null;
    navHandlerLeft = null;
    navHandlerRight = null;
}

// Initialisation
export default function nonInit() {
    const itemsEl = document.querySelectorAll('.carousel-non-item');
    titleElement = document.querySelector('.carousel-non-title');

    if (itemsEl.length === 0) return;

    // Réinitialiser les variables
    isDestroyed = false;
    currentIndex = slideHistory > 0 ? slideHistory : 0; // Restaurer la position sauvegardée
    items = [];
    positionsData = [];
    isAnimating = false;

    // Nettoyer les anciens event listeners si présents
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
    }

    let btnLeft = document.querySelector('.btn-abcdr-du-non-left');
    let btnRight = document.querySelector('.btn-abcdr-du-non-right');

    if (btnLeft && navHandlerLeft) {
        btnLeft.removeEventListener('click', navHandlerLeft);
    }
    if (btnRight && navHandlerRight) {
        btnRight.removeEventListener('click', navHandlerRight);
    }

    // Stocker les éléments avec leurs données
    itemsEl.forEach((el, index) => {
        const titleText = el.querySelector('img')?.alt || '';
        items.push({
            element: el,
            index: index,
            title: titleText
        });
    });

    // Calculer les positions sur l'arc de cercle (toujours 5 positions visibles)
    calculateArcPositions();

    // Event listeners
    btnLeft = document.querySelector('.btn-abcdr-du-non-left');
    btnRight = document.querySelector('.btn-abcdr-du-non-right');

    navHandlerLeft = () => navigate('left');
    navHandlerRight = () => navigate('right');

    if (btnLeft) btnLeft.addEventListener('click', navHandlerLeft);
    if (btnRight) btnRight.addEventListener('click', navHandlerRight);

    // Recalculer les positions au redimensionnement
    resizeHandler = () => {
        calculateArcPositions();
        updateCarousel(false);
    };
    window.addEventListener('resize', resizeHandler);

    // Initialisation
    updateCarousel(false);
}

// Calcule les 7 positions de l'arc de cercle (5 visibles + 2 cachées aux extrémités)
function calculateArcPositions() {
    const container = document.querySelector('.carousel-non-arc');
    if (!container) return;

    const containerWidth = container.offsetWidth;
    const centerX = containerWidth / 2;

    // Rayon de l'arc (ajusté pour mobile/desktop)
    const radius = containerWidth < 768 ? containerWidth * 1.2 : containerWidth * 0.60;

    // 7 positions sur l'arc (dont 2 cachées)
    const positions = [];
    const angleStart = 170; // Degré de départ (plus à gauche pour position cachée)
    const angleEnd = 10; // Degré de fin (plus à droite pour position cachée)
    const angleStep = (angleStart - angleEnd) / 6; // 6 intervalles pour 7 positions

    // Calculer d'abord toutes les positions pour trouver le Y minimum
    const tempPositions = [];
    for (let i = 0; i < 7; i++) {
        const angle = (angleStart - (i * angleStep)) * (Math.PI / 180);
        const x = centerX + radius * Math.cos(angle);
        const y = radius * Math.sin(angle);
        tempPositions.push({ x, y, angle });
    }

    // Trouver le Y maximum (position la plus haute, qui est au centre)
    const maxY = Math.max(...tempPositions.map(pos => pos.y));

    // Recalculer les positions RELATIVES à la position centrale (index 3 maintenant)
    const centerPos = tempPositions[3];

    for (let i = 0; i < 7; i++) {
        positions.push({
            x: tempPositions[i].x - centerPos.x, // Position relative au centre
            y: (maxY - tempPositions[i].y) - (maxY - centerPos.y), // Position relative au centre
            angle: tempPositions[i].angle,
            rotation: i === 3 ? 0 : (i - 3) * 20 // Rotation progressive
        });
    }

    positionsData = positions;
}

// Navigation
function navigate(direction) {
    if (isAnimating || items.length === 0) return;

    isAnimating = true;

    // Flèche droite -> la vignette de droite vient au centre (index augmente)
    // Flèche gauche -> la vignette de gauche vient au centre (index diminue)
    if (direction === 'right') {
        currentIndex = (currentIndex + 1) % items.length;
    } else {
        currentIndex = (currentIndex - 1 + items.length) % items.length;
    }

    updateCarousel(true);

    // Sauvegarder la position actuelle
    slideHistory = currentIndex;

    setTimeout(() => {
        isAnimating = false;
    }, 600);
}

// Mise à jour du carrousel
function updateCarousel(animate = true) {
    // Ne rien faire si le module est détruit
    if (isDestroyed || items.length === 0 || positionsData.length === 0) return;

    // Obtenir les items à afficher dans l'ordre
    const visibleIndices = getVisibleIndices();

    items.forEach((item, index) => {
        const positionIndex = visibleIndices.indexOf(index);

        if (positionIndex !== -1) {
            // Mapper l'index de position à l'index correspondant dans positionsData (qui a toujours 7 positions)
            // Si on a 5 items, on utilise les positions 1,2,3,4,5 (en sautant 0 et 6)
            // Si on a 7 items, on utilise toutes les positions 0,1,2,3,4,5,6
            const numPositions = visibleIndices.length;
            const offset = Math.floor((7 - numPositions) / 2); // Décalage pour centrer
            const actualPositionIndex = positionIndex + offset;

            const pos = positionsData[actualPositionIndex];

            // Position centrale dynamique selon le nombre d'items
            const centerIndex = Math.floor(visibleIndices.length / 2);
            const isCentered = positionIndex === centerIndex;

            // Positions cachées = première et dernière (seulement si on a 7 items)
            const isHidden = visibleIndices.length === 7 && (positionIndex === 0 || positionIndex === 6);

            // Retirer la classe center de tous
            item.element.classList.remove('center');

            // Ajouter la classe center à l'élément central
            if (isCentered) {
                item.element.classList.add('center');
            }

            // Si l'élément n'était pas actif avant, le placer instantanément à sa position
            // pour éviter qu'il s'anime depuis une position lointaine
            const wasActive = item.element.classList.contains('active');
            if (!wasActive) {
                gsap.set(item.element, {
                    x: pos.x,
                    y: pos.y,
                    rotation: pos.rotation,
                    opacity: 0
                });
            }

            // Animation - positions cachées = opacity 0, visibles = 0.7, centre = 1
            const targetOpacity = isHidden ? 0 : (isCentered ? 1 : 0.7);

            const props = {
                x: pos.x, // Position relative (0 pour le centre, offset pour les autres)
                y: pos.y,
                rotation: pos.rotation,
                opacity: targetOpacity,
                duration: animate ? 0.6 : 0,
                ease: "power2.inOut"
            };

            gsap.to(item.element, props);
            item.element.classList.add('active');

        } else {
            // L'item n'est pas visible - le masquer instantanément
            item.element.classList.remove('active', 'center');

            // Masquer immédiatement sans animation ET sans déplacer l'élément
            // pour éviter de voir la transition de l'élément qui "saute"
            gsap.set(item.element, {
                opacity: 0,
                immediateRender: true
            });
        }
    });

    // Mettre à jour le titre
    if (titleElement) {
        const currentItem = items[currentIndex];
        if (animate) {
            gsap.to(titleElement, {
                opacity: 0,
                duration: 0.2,
                onComplete: () => {
                    if (!isDestroyed && titleElement) {
                        titleElement.textContent = currentItem.title;
                        gsap.to(titleElement, { opacity: 1, duration: 0.3 });
                    }
                }
            });
        } else {
            titleElement.textContent = currentItem.title;
        }
    }
}

// Obtenir les indices des items à afficher (max 7, adapté au nombre d'items)
function getVisibleIndices() {
    const indices = [];
    const totalItems = items.length;

    // Si moins de 7 items, adapter le nombre de positions
    const maxPositions = Math.min(7, totalItems);
    const halfPositions = Math.floor(maxPositions / 2);

    // Afficher les items autour de currentIndex
    for (let i = -halfPositions; i <= halfPositions; i++) {
        // Ne pas ajouter de doublons - vérifier que l'index n'est pas déjà présent
        let index = (currentIndex + i + totalItems) % totalItems;
        if (!indices.includes(index)) {
            indices.push(index);
        }
    }

    return indices;
}
